import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '@/api/client';
import { equipment, type EquipmentDTO } from '@/api/endpoints';
import { MATERIALS } from '@/game/data';
import { useSession } from '@/store/session';
import { Icon } from '@/components/ui/Icon';
import { GearIcon, gearKeyForAxe, gearKeyForLamp } from '@/components/ui/GearIcon';

/** Axe and lamp upgrades, plus the material table that explains the axe gate. */
export function Shop() {
  const [gear, setGear] = useState<Record<string, EquipmentDTO> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const applyWallet = useSession((s) => s.applyWallet);
  const refresh = useSession((s) => s.refresh);
  const axeLevel = gear?.axe?.level ?? 1;

  const load = () => void equipment.list().then(setGear);
  useEffect(load, []);

  async function upgrade(key: 'axe' | 'lamp' | 'backpack') {
    setBusy(key); setError(null);
    try {
      const res = await equipment.upgrade(key);
      applyWallet(res.wallet);
      load();
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upgrade failed.');
    } finally {
      setBusy(null);
    }
  }

  if (!gear) return <p className="py-6 text-center text-[11px] text-ink-dim">Loading…</p>;

  return (
    <div className="h-full overflow-y-auto px-3 pb-6 pt-3.5">
      {error && <p role="alert" className="mb-2 text-xs text-[var(--danger)]">{error}</p>}

      <Card gear={gear.axe} title="AXE · BREAKING POWER" icon={<Icon name="axe" size={28} />} kind="axe"
            note="Your axe decides which material you can break at all. A weaker axe bounces off."
            busy={busy === 'axe'} onUpgrade={() => void upgrade('axe')} />

      <section className="mt-3 rounded-md border border-[var(--hairline)] bg-[var(--surface)] p-3.5">
        <h2 className="font-pixel text-[9px] text-torch-core">ROCK HARDNESS TABLE</h2>
        <ul className="mt-2" role="list">
          {MATERIALS.map((m) => {
            const can = axeLevel >= m.reqAxe;
            return (
              <li key={m.key}
                  className={`flex items-center gap-2.5 border-t border-[rgba(245,197,66,.08)]
                              py-1.5 text-[11.5px] first:border-t-0 ${can ? '' : 'opacity-50'}`}>
                <span className="h-4.5 w-4.5 shrink-0 rounded-md border border-black/50"
                      style={{ background: `linear-gradient(135deg,${m.colorA},${m.colorB})` }}
                      aria-hidden />
                <span className="flex-1 font-semibold">{m.name}</span>
                <span className="text-[11px] text-ink-dim">×{m.hp.toFixed(2)} HP</span>
                <span className={`font-pixel text-[6.5px] ${can ? 'text-[var(--ok)]' : 'text-[var(--danger)]'}`}>
                  {can ? 'CAN BREAK' : `AXE LV.${m.reqAxe}`}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="mt-2.5 text-[11px] text-ink-dim">
          Harder rock costs more swings but rolls a much better treasure chance — that is the whole
          reason to upgrade.
        </p>
      </section>

      <Card gear={gear.lamp} title="LAMP · HOW DEEP YOU CAN WALK" icon={<Icon name="lamp" size={28} />} kind="lamp"
            note="Past the limit the tunnel is pitch black and the step is refused."
            busy={busy === 'lamp'} onUpgrade={() => void upgrade('lamp')} />

      {gear.backpack && (
        <Card gear={gear.backpack} title="BACKPACK · CARRY CAPACITY" icon={<Icon name="bag" size={28} />}
              note="How much loot you can hold before you must sell."
              busy={busy === 'backpack'} onUpgrade={() => void upgrade('backpack')} />
      )}
    </div>
  );
}

function Card({ gear, title, icon, kind, note, busy, onUpgrade }: {
  gear: EquipmentDTO | undefined; title: string; icon: ReactNode; note: string;
  /** Which atlas row to pull the level render from; omit for gear with no art. */
  kind?: 'axe' | 'lamp';
  busy: boolean; onUpgrade: () => void;
}) {
  if (!gear) return null;
  const next = gear.next;
  const depth = gear.stats.max_depth_m;
  const depthLabel = depth == null ? null : depth < 0 ? 'endless' : `${depth}m`;

  return (
    <section className="mt-3 rounded-md border border-[var(--hairline)] bg-[var(--surface)] p-3.5">
      <h2 className="font-pixel text-[9px] text-torch-core">{title}</h2>
      <header className="mt-2.5 flex items-center gap-3">
        {/* The level's own render, not one emoji for the whole ladder — a Wood
            Axe and a Shwe Axe used to look identical here. */}
        <span className="grid h-13 w-13 place-items-center rounded-xl border border-ui-lit
                         bg-[linear-gradient(#3B2917,#1D1309)] p-1.5">
          <GearIcon
            gearKey={kind === 'axe' ? gearKeyForAxe(gear.level)
              : kind === 'lamp' ? gearKeyForLamp(gear.level) : ''}
            fallback={icon}
            size={42}
          />
        </span>
        <div>
          <p className="font-extrabold">{gear.name}</p>
          <p className="font-pixel text-[7px] text-ink-dim">
            LEVEL {gear.level}{gear.max_level ? ` / ${gear.max_level}` : ''}
          </p>
        </div>
      </header>

      <div className="mt-2.5 grid grid-cols-2 gap-1.5 text-[11px]">
        {gear.stats.damage != null && (
          <Row k="Damage" v={gear.stats.damage} next={next?.stats.damage} />
        )}
        {gear.stats.crit_pct != null && (
          <Row k="Crit" v={`${gear.stats.crit_pct}%`} next={next && `${next.stats.crit_pct}%`} />
        )}
        {/*
          * Loot bonus was not shown at all, and for the top axes it is the ENTIRE
          * benefit: 9->10 buys +7pp crit and +13pp loot, the biggest jump in both on
          * the whole ladder. The main reason to buy it was invisible.
          */}
        {gear.stats.loot_bonus_pct != null && (
          <Row k="Loot bonus" v={`+${gear.stats.loot_bonus_pct}%`}
               next={next && `+${next.stats.loot_bonus_pct}%`} />
        )}
        {depthLabel && (
          <Row k="Depth limit" v={depthLabel}
               next={next && (next.stats.max_depth_m == null || next.stats.max_depth_m < 0
                 ? 'endless' : `${next.stats.max_depth_m}m`)} />
        )}
        {/*
          * How far ONE torch actually goes, next to the cap.
          *
          * "Depth limit" is permission, not reach: fuel burns faster the deeper you
          * are, so from the Torch up every lamp's cap sits beyond a full tank — the
          * Spirit Lamp caps at 870 m and one torch reaches about 630. Both numbers
          * are true, and showing only the cap let someone spend 1.8M coins without
          * knowing it was a refuelling target rather than a promise.
          *
          * Server-computed (`reach_m`, from the run engine's own fuel curve) because
          * a client-side copy of that curve would be a second opinion about the
          * server's physics.
          */}
        {gear.stats.reach_m != null && (
          <Row k="One torch reaches" v={`${gear.stats.reach_m}m`}
               next={next?.stats.reach_m != null ? `${next.stats.reach_m}m` : undefined} />
        )}
        {gear.stats.light_px != null && (
          <Row k="Light" v={`${gear.stats.light_px}px`} next={next && `${next.stats.light_px}px`} />
        )}
        {gear.stats.slots != null && (
          <Row k="Slots" v={gear.stats.slots} next={next?.stats.slots} />
        )}
      </div>

      <p className="mt-2 text-[11px] text-ink-dim">{note}</p>

      {next ? (
        <button type="button" onClick={onUpgrade} disabled={busy}
          className="mt-2.5 w-full rounded-sm border border-[rgba(255,220,170,.5)]
                     bg-[linear-gradient(#FFA945,#E0761C)] py-3 font-extrabold text-[#2A1503]
                     shadow-[0_4px_0_-1px_#8C440C] active:translate-y-0.5 disabled:opacity-50">
          {busy ? 'Upgrading…' : (
            <span className="inline-flex items-center justify-center gap-1.5">
              {`Upgrade to ${next.name} ·`}
              {/* Coins are money. A currency drawn as an OS emoji is whatever font
                  the device ships, and `Icon.test.ts` exists precisely so coin,
                  kyat and rock stay three distinguishable silhouettes. */}
              <Icon name="coin" size={15} />
              {next.cost_coins?.toLocaleString('en-US')}
            </span>
          )}
        </button>
      ) : (
        <button type="button" disabled
          className="mt-2.5 w-full rounded-sm border border-ui-lit bg-black/30 py-3 font-bold opacity-50">
          Fully upgraded
        </button>
      )}
    </section>
  );
}

function Row({ k, v, next }: {
  k: string; v: string | number; next?: string | number | null | undefined;
}) {
  return (
    <div className="flex items-baseline justify-between gap-1.5 rounded-lg bg-black/30 px-2.5 py-2">
      <span className="text-ink-dim">{k}</span>
      <b className="tabular text-ink-hi">
        {v}{next != null && next !== v && <span className="text-[var(--ok)]"> → {next}</span>}
      </b>
    </div>
  );
}
